import { Children, isValidElement, useMemo, useRef } from 'react';
import { Select, chakraComponents } from 'chakra-react-select';
import { useVirtualizer } from '@tanstack/react-virtual';

const optionHeight = 36;
const virtualizeAboveCount = 50;

const getGroupOptions = (item) => {
  const groupOptions = item?.props?.data?.options;
  return Array.isArray(groupOptions) ? groupOptions : null;
};

const countOptions = (items) =>
  items.reduce((sum, item) => {
    const groupOptions = getGroupOptions(item);
    return sum + (groupOptions ? groupOptions.length : 1);
  }, 0);

const flattenChildren = (children) => {
  const rows = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    if (getGroupOptions(child)) {
      rows.push({ type: 'heading', groupElement: child });
      Children.forEach(child.props.children, (optionElement) => {
        if (isValidElement(optionElement)) {
          rows.push({ type: 'option', element: optionElement });
        }
      });
    } else {
      rows.push({ type: 'option', element: child });
    }
  });
  return rows;
};

const VirtualizedInnerList = ({ children, maxHeight, innerRef, ...rest }) => {
  const scrollRef = useRef(null);

  const rows = useMemo(() => flattenChildren(children), [children]);

  const setRefs = (element) => {
    scrollRef.current = element;
    if (typeof innerRef === 'function') {
      innerRef(element);
    }
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => optionHeight,
    getScrollElement: () => scrollRef.current,
    overscan: 20,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <chakraComponents.MenuList {...rest}>
      <div
        ref={setRefs}
        style={{
          height: `${maxHeight}px`,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ height: `${totalSize}px`, position: 'relative' }}>
          {virtualItems.map((virtualItem) => {
            const row = rows[virtualItem.index];
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {row?.type === 'heading' ? (
                  <chakraComponents.GroupHeading
                    {...row.groupElement.props.headingProps}
                    selectProps={row.groupElement.props.selectProps}
                    theme={row.groupElement.props.theme}
                    cx={row.groupElement.props.cx}
                    getStyles={row.groupElement.props.getStyles}
                    getClassNames={row.groupElement.props.getClassNames}
                  >
                    {row.groupElement.props.label}
                  </chakraComponents.GroupHeading>
                ) : (
                  row?.element
                )}
              </div>
            );
          })}
        </div>
      </div>
    </chakraComponents.MenuList>
  );
};

const VirtualizedMenuList = (props) => {
  const items = Array.isArray(props.children) ? props.children : [];

  if (countOptions(items) < virtualizeAboveCount) {
    return <chakraComponents.MenuList {...props} />;
  }

  return <VirtualizedInnerList {...props}>{items}</VirtualizedInnerList>;
};

const groupFlatItems = (items, groupBy) => {
  const itemsByCategory = [];
  const itemsWithoutCategory = [];

  items.forEach((item) => {
    const category = item?.[groupBy];

    if (!category) {
      itemsWithoutCategory.push(item);
      return;
    }

    let group = itemsByCategory.find((g) => g.label === category);

    if (!group) {
      group = {
        label: category,
        options: [],
      };

      itemsByCategory.push(group);
    }

    group.options.push(item);
  });

  return [...itemsWithoutCategory, ...itemsByCategory];
};

const toSelectedValue = (value, options, isMulti) => {
  if (!value) {
    return isMulti ? [] : null;
  }

  const allOptions = options.flatMap((option) => (option.options ? option.options : [option]));

  const optionsByValue = new Map(allOptions.map((option) => [option.value, option]));

  const valuesToMatch = Array.isArray(value) ? value : [value];

  const matchedOptions = valuesToMatch.map((val) => optionsByValue.get(val)).filter(Boolean);

  return isMulti ? matchedOptions : matchedOptions[0] || null;
};

const chakraStyles = {
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    paddingInlineStart: '0',
    paddingInlineEnd: '0.5rem',
    background: 'transparent',
  }),

  control: (provided) => ({
    ...provided,
    borderRadius: 'var(--chakra-radii-md)',
    backgroundColor: 'var(--chakra-colors-white)',
  }),

  menu: (provided) => ({
    ...provided,
    zIndex: 10,
  }),

  groupHeading: (provided) => ({
    ...provided,
    fontWeight: '700',
    color: 'var(--chakra-colors-gray-500)',
    background: 'var(--chakra-colors-white)',
  }),
};

const VSelect = ({
  placeholder,
  collection,
  value,
  onValueChange,
  multiple,
  searchable = true,
  virtualized = false,
  groupBy,
  ref = undefined,
  ...props
}) => {
  const options = useMemo(() => {
    const items = collection?.items ?? [];
    return groupBy ? groupFlatItems(items, groupBy) : items;
  }, [collection, groupBy]);

  const selectedValue = toSelectedValue(value, options, multiple);

  const handleChange = (selected) => {
    const items = [].concat(selected || []);
    onValueChange?.({
      value: items.map((item) => item.value),
      items,
    });
  };

  const componentOverrides = useMemo(() => {
    const overrides = {};
    if (virtualized) {
      overrides.MenuList = VirtualizedMenuList;
    }
    if (groupBy) {
      overrides.GroupHeading = chakraComponents.GroupHeading;
    }
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  }, [virtualized, groupBy]);

  return (
    <Select
      ref={ref}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      isMulti={multiple}
      placeholder={placeholder}
      isSearchable={searchable}
      selectedOptionStyle="check"
      selectedOptionColorScheme="primary"
      chakraStyles={chakraStyles}
      components={componentOverrides}
      {...props}
    />
  );
};

export default VSelect;
